import { CLIENT_ID_KEY, PROJECT_ID_KEY, FlagSet } from '../models/feature-flag';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { LDClient, LDUser, LDFlagSet, LDFlagValue, initialize } from 'launchdarkly-js-client-sdk';
import { Auth, authSelectors } from '@content-platform/web-angular-shared-util-auth';
import { Configuration } from '@content-platform/web-angular-shared-util-configuration';

import { filter, map, switchMap } from 'rxjs/operators';
import { fromFeatureFlags } from '../reducers';
import { featureFlagActions } from '../actions';
import { featureFlagSelectors } from '../selectors';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';

@Injectable({
  providedIn: 'root',
})
export class WebAngularSharedDataAccessFeatureFlagApisService {
  public appClient: LDClient;
  public globalClient: LDClient;
  // private globalClientId;
  private projectId;

  constructor(
    private store: Store<fromFeatureFlags.State>,
    private authStore: Store<Auth>,
    private logger: LoggerService
  ) {
    this.logger = logger.instance('WebAngularSharedDataAccessFeatureFlagApisService');
  }

  /**
   * Retrieves ad user and client key to initialize launchdarkly client
   * @param projectKey unique project key as mentioned in Launchdarkly config
   */
  public configureClient(): void {
    const ldConfig = Configuration.get('launchDarkly');
    // const defaultConfig = Configuration.getDefaultConfig();
    const genericUser = ldConfig['genericUser'];

    if (!ldConfig) {
      this.logger.error('Could not find LaunchDarkly settings');
    }

    // this.globalClientId = ldConfig[GLOBAL_PROJECT_KEY];
    this.projectId = ldConfig[PROJECT_ID_KEY];
    const ldClientKey = ldConfig[CLIENT_ID_KEY];
    const flags: FlagSet = ldConfig['flags'];
    // const globalFlags: FlagSet = defaultConfig['launchDarkly']['flags'];

    if (!this.projectId || !ldClientKey) {
      this.logger.error('Invalid LaunchDarkly settings', { launchDarkly: ldConfig });
    }

    if (genericUser) {
      this.setClient(ldClientKey, genericUser, flags);
    } else {
      this.authStore
        .pipe(
          select(authSelectors.getADUser),
          filter((user) => !!user)
        )
        .subscribe((user) => {
          const ldUser: LDUser = {
            key: user.profile.oid,
            name: user.profile.name,
            email: user.profile.unique_name,
          };

          this.setClient(ldClientKey, ldUser, flags);
        });
    }
  }

  private setClient(ldClientKey: any, ldUser: LDUser, flags: FlagSet) {
    this.appClient = this.initializeClient(ldClientKey, ldUser, this.projectId, flags);
  }

  public loadFlagsForUser(user: LDUser) {
    this.appClient.identify(user, null, (err, flags: LDFlagSet) => {
      if (err) {
        this.logger.error('launchdarkly initialization failed with error: ', err);
        return;
      }
      this.store.dispatch(
        new featureFlagActions.SetFlags({
          flags: flags,
          project: this.projectId,
        })
      );
    });
  }

  /**
   * Initializes feature flag client
   * @param clientKey launchdarkly project specific client key
   * @param userObject  launchdarkly user object
   * @param projectKey launchDarkly project identifier
   */
  private initializeClient(
    clientKey: string,
    userObject: unknown,
    projectKey: string,
    flags: FlagSet
  ): LDClient {
    const client = initialize(clientKey, userObject, {
      evaluationReasons: true,
      bootstrap: 'localStorage',
    });

    client
      .waitForInitialization()
      .then(() => {
        this.store.dispatch(
          new featureFlagActions.SetFlags({
            flags: this.appClient.allFlags(),
            project: projectKey,
          })
        );
      })
      .catch((err) => {
        this.store.dispatch(
          new featureFlagActions.SetFlags({
            flags: flags,
            project: projectKey,
          })
        );
        this.logger.error('launchdarkly initialization failed with error: ', err);
      });

    client.on('change', (changes) => {
      this.store.dispatch(
        new featureFlagActions.UpdateFlags({ changes: changes, project: projectKey })
      );
    });

    return client;
  }

  /**
   * Retrieves all feature flags
   * @returns feature flags
   */
  private getAllFlags(): LDFlagSet {
    if (!this.appClient) {
      const ldConfig = Configuration.get('launchDarkly');
      return ldConfig.flags;
    }

    return this.appClient.allFlags();
  }

  /**
   * Returns the value of the flag
   * Checks if the flag is available app level first and then global level
   * Otherwise throws an error
   * @param flagname
   * @param flaglevel
   */
  public getFlag(flagname: string): LDFlagValue {
    const flags = this.getAllFlags();
    if (flags[flagname] !== undefined) {
      return flags[flagname];
    } else {
      this.logger.error(`NOT_FOUND: ${flagname} does not exists`);
      return false;
    }
  }

  /**
   * Returns an observable to retrieve the value of the flag
   * Checks if the flag is available app level first and then global level
   * Otherwise throws an error
   * @param flagname
   * @param flaglevel
   */
  // tslint:disable-next-line:no-any
  public getFlagAsync(flagname: string): Observable<any> {
    const appFlag$ = this.store.select(featureFlagSelectors.getAppFeatureFlag({ flag: flagname }));
    return this.isFeatureFlagsInitializationComplete().pipe(
      filter((initializationComplete) => {
        console.log('initializationComplete', initializationComplete);
        return !!initializationComplete;
      }),
      switchMap(() =>
        combineLatest([appFlag$]).pipe(
          map(([appRes]) => {
            if (appRes !== undefined) {
              return appRes;
            } else {
              this.logger.error(`NOT_FOUND: ${flagname} does not exists`);
              return false;
            }
          })
        )
      )
    );
  }

  public isFeatureFlagsInitializationComplete(): Observable<boolean> {
    // TODO - remove after dev testing
    this.store.select(featureFlagSelectors.getFeatureFlagInitializationStatus)
      .subscribe(res => {
        console.log('isFeatureFlagsInitializationComplete', res);
      });
    return this.store.select(featureFlagSelectors.getFeatureFlagInitializationStatus);
  }

  public shutdownClient() {
    this.appClient.close();
  }

  public trackEvent(eventName: string, prop) {
    this.appClient.track(eventName, prop);
  }

  public getAsyncFlagVariation(flagname: string, variation?: any) {
    const appFlag$ = this.store.select(featureFlagSelectors.getAppFeatureFlag({ flag: flagname }));
    return this.isFeatureFlagsInitializationComplete().pipe(
      filter((initializationComplete) => !!initializationComplete),
      switchMap(() =>
        combineLatest([appFlag$]).pipe(
          map(([appRes]) => {
            if (appRes !== undefined && variation) {
              return this.appClient.variation(flagname, variation);
            }
            return this.appClient.variation(flagname, undefined);
          })
        )
      )
    );
  }

  public getAsyncFlagVariationDetails(flagname: string, defaultValue: any) {
    const appFlag$ = this.store.select(featureFlagSelectors.getAppFeatureFlag({ flag: flagname }));
    return this.isFeatureFlagsInitializationComplete().pipe(
      filter((initializationComplete) => !!initializationComplete),
      switchMap(() =>
        combineLatest([appFlag$]).pipe(
          map(([appRes]) => {
            if (appRes !== undefined && defaultValue) {
              return this.appClient.variationDetail(flagname, defaultValue);
            }
            return undefined;
          })
        )
      )
    );
  }
}
